import i18n from '@/utils/i18n';
import React, { useCallback, useMemo, useState } from 'react';
import { createStyles } from '@/contexts/theme-context';
import {
  formatRangeBoundText,
  usePricingFilterRangeInput,
} from '@sales-portal/domain/pricing/blocs/hooks/use-pricing-filter-range-input';
import {
  fromSliderRange,
  getRangeSliderStep,
  toSliderRange,
} from '@sales-portal/domain/pricing/blocs/utils/pricing-filter-range-slider';
import { PricingFilterClearButton } from '@sales-portal/domain/pricing/components/pricing-filter-clear-button';
import { PricingFilterRangeBoundInput } from '@sales-portal/domain/pricing/components/pricing-filter-range-bound-input';
import {
  PricingRangeBound,
  RangeFilterSection,
} from '@sales-portal/domain/pricing/blocs/utils/pricing-filter-types';
import { PricingRangeMode } from '@sales-portal/domain/pricing/blocs/utils/pricing-filter-range-modes';
import {
  SegmentedControl,
  Slider,
  SliderRange,
} from '@usepepper/sprout-kit-ui';
import { Typography } from '@usepepper/ui/atoms/typography';
import { View } from 'react-native';

type RangeSectionData = Omit<RangeFilterSection, 'getValue'>;
type RangeBoundKey = 'max' | 'min';

// Present only on a section that is one half of a $/% pair.
export interface RangeModeToggle {
  currencyAffix: string;
  mode: PricingRangeMode;
  onModeChange: (mode: PricingRangeMode) => void;
}

interface Props {
  bound: PricingRangeBound;
  modeToggle?: RangeModeToggle;
  onClear: () => void;
  onRangeChange: (bound: RangeBoundKey, value: number | null) => void;
  section: RangeSectionData;
}

export const PricingFilterRangeSection: React.FC<Props> = ({
  bound,
  modeToggle,
  onClear,
  onRangeChange,
  section,
}) => {
  const [styles, theme] = useStyles();

  // Thumb position mid-drag. Null between drags, so an external change to
  // `bound` (Clear, a Min/Max edit) moves the thumbs.
  const [dragValue, setDragValue] = useState<SliderRange | null>(null);

  const isActive = bound.min !== null || bound.max !== null;

  const hasSliderTrack = section.bounds.max > section.bounds.min;

  const sliderValue = useMemo(
    () => dragValue ?? toSliderRange(bound, section.bounds),
    [bound, dragValue, section.bounds],
  );
  const sliderStep = useMemo(
    () => getRangeSliderStep(section.bounds),
    [section.bounds],
  );

  // The fields follow the thumbs while dragging, so both readouts agree
  // before the drag commits.
  const displayBound = useMemo(
    () => (dragValue ? fromSliderRange(dragValue, section.bounds) : bound),
    [bound, dragValue, section.bounds],
  );

  const min = usePricingFilterRangeInput(
    displayBound.min,
    displayBound.max,
    'min',
    (value) => onRangeChange('min', value),
  );
  const max = usePricingFilterRangeInput(
    displayBound.max,
    displayBound.min,
    'max',
    (value) => onRangeChange('max', value),
  );

  const handleSliderCommit = useCallback(
    (next: SliderRange) => {
      setDragValue(null);

      const committedBound = fromSliderRange(
        toSliderRange(bound, section.bounds),
        section.bounds,
      );
      const nextBound = fromSliderRange(next, section.bounds);
      if (nextBound.min !== committedBound.min) {
        onRangeChange('min', nextBound.min);
      }
      if (nextBound.max !== committedBound.max) {
        onRangeChange('max', nextBound.max);
      }
    },
    [bound, onRangeChange, section.bounds],
  );

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Typography variant="label-md">{section.title}</Typography>
        {isActive ? <PricingFilterClearButton onPress={onClear} /> : null}
      </View>
      {hasSliderTrack ? (
        <Slider
          aria-label={[
            i18n.t('pricing.filters.slider.min', { title: section.title }),
            i18n.t('pricing.filters.slider.max', { title: section.title }),
          ]}
          formatValue={(value) =>
            `${formatRangeBoundText(value)}${section.affix}`
          }
          height={theme.sk.spacing.spaceXlarge}
          max={section.bounds.max}
          min={section.bounds.min}
          onValueChange={setDragValue}
          onValueCommit={handleSliderCommit}
          size="$sm"
          step={sliderStep}
          value={sliderValue}
          width="100%"
        />
      ) : null}
      <View style={styles.inputRow}>
        {modeToggle ? (
          <View style={styles.modeToggle}>
            <SegmentedControl
              aria-label={i18n.t('pricing.filters.mode.label', {
                title: section.title,
              })}
              items={[
                { label: '%', value: PricingRangeMode.Percent },
                {
                  label: modeToggle.currencyAffix,
                  value: PricingRangeMode.Currency,
                },
              ]}
              onValueChange={(value) =>
                modeToggle.onModeChange(value as PricingRangeMode)
              }
              size="$sm"
              value={modeToggle.mode}
            />
          </View>
        ) : null}
        <View style={styles.input}>
          <PricingFilterRangeBoundInput
            accessibilityLabel={`${section.title} ${i18n.t('pricing.filters.min')}`}
            affix={section.affix}
            label={i18n.t('pricing.filters.min')}
            onBlur={min.handleBlur}
            onChangeText={min.handleChangeText}
            placeholder={formatRangeBoundText(section.bounds.min)}
            unit={section.unit}
            value={min.text}
          />
        </View>
        <View style={styles.input}>
          <PricingFilterRangeBoundInput
            accessibilityLabel={`${section.title} ${i18n.t('pricing.filters.max')}`}
            affix={section.affix}
            label={i18n.t('pricing.filters.max')}
            onBlur={max.handleBlur}
            onChangeText={max.handleChangeText}
            placeholder={formatRangeBoundText(section.bounds.max)}
            unit={section.unit}
            value={max.text}
          />
        </View>
      </View>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  container: {
    gap: theme.sk.spacing.spaceSmall,
    paddingBottom: theme.sk.spacing.spaceLarge,
    paddingTop: theme.sk.spacing.spaceLarge,
  },
  header: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  input: {
    flex: 1,
  },
  inputRow: {
    alignItems: 'flex-end',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSmall,
  },
  modeToggle: {
    justifyContent: 'center',
  },
}));
